import { RecognitionStatus } from '@pflegia/types/common/candidate/recognition-status';

import { FunnelQuestion } from '../funnel/questions';
import { B_PRIORITIES, C_PRIORITIES, CandidatePriority, D_PRIORITIES } from './candidate-priority';

export const TRANSLATED_TRAINING_RECOGNITION_STATUS = {
  [RecognitionStatus.NotYetApplied]: 'Noch nicht beantragt',
  [RecognitionStatus.Completed]: 'Anerkennung abgeschlossen',
  [RecognitionStatus.InProgress]: 'Im Prozess der Anerkennung',
};

export interface QualificationRecognition {
  status?: RecognitionStatus;
  date?: Date;
}

export type PreRecognitionQuestion =
  | FunnelQuestion.BQualification
  | FunnelQuestion.CQualification
  | FunnelQuestion.DQualification
  | FunnelQuestion.JobQualification;

export type RecognitionQuestions =
  | FunnelQuestion.BQualificationRecognition
  | FunnelQuestion.CQualificationRecognition
  | FunnelQuestion.DQualificationRecognition
  | FunnelQuestion.JobQualificationRecognition;

export type DetailedQualificationRecognition = Partial<Record<PreRecognitionQuestion, QualificationRecognition>>;

export const recognitionSelection = {
  [FunnelQuestion.BQualification]: { status: true, date: true },
  [FunnelQuestion.CQualification]: { status: true, date: true },
  [FunnelQuestion.DQualification]: { status: true, date: true },
};

export const QUALIFICATION_RECOGNITION_QUESTIONS = [
  FunnelQuestion.BQualificationRecognition,
  FunnelQuestion.CQualificationRecognition,
  FunnelQuestion.DQualificationRecognition,
] as FunnelQuestion[];

export const QUESTIONS_WITH_QUALIFICATION_RECOGNITION = [
  FunnelQuestion.BQualification,
  FunnelQuestion.CQualification,
  FunnelQuestion.DQualification,
] as PreRecognitionQuestion[];

export const QUALIFICATION_TO_RECOGNITION: Record<PreRecognitionQuestion, RecognitionQuestions> = {
  [FunnelQuestion.BQualification]: FunnelQuestion.BQualificationRecognition,
  [FunnelQuestion.CQualification]: FunnelQuestion.CQualificationRecognition,
  [FunnelQuestion.DQualification]: FunnelQuestion.DQualificationRecognition,
  [FunnelQuestion.JobQualification]: FunnelQuestion.JobQualificationRecognition,
};

/**
 * Maps a candidate's priority to the qualification key its recognition should live under.
 * Recognition is only meaningful for B, C and D priorities.
 */
export const getEffectiveRecognitionQuestion = (priority?: CandidatePriority | null): PreRecognitionQuestion | undefined => {
  if (!priority) return undefined;
  if (B_PRIORITIES.includes(priority)) return FunnelQuestion.BQualification;
  if (C_PRIORITIES.includes(priority)) return FunnelQuestion.CQualification;
  if (D_PRIORITIES.includes(priority)) return FunnelQuestion.DQualification;
  return undefined;
};

/**
 * Resolves the qualification key the candidate's current recognition lives under.
 * Prefers the priority-derived key when it holds a status, otherwise the first key that does
 * (handles historically or manually mis-keyed data), otherwise the priority-derived key so that
 * a new value is written where prioritization and normalization expect it.
 */
export const resolveCurrentRecognitionQuestion = (
  qualificationRecognition?: DetailedQualificationRecognition | null,
  priority?: CandidatePriority | null,
): PreRecognitionQuestion | undefined => {
  const effectiveQuestion = getEffectiveRecognitionQuestion(priority);
  if (effectiveQuestion && qualificationRecognition?.[effectiveQuestion]?.status) return effectiveQuestion;

  return QUESTIONS_WITH_QUALIFICATION_RECOGNITION.find(question => qualificationRecognition?.[question]?.status) ?? effectiveQuestion;
};

/**
 * Resolves the candidate's current recognition regardless of which qualification key it is stored under.
 * Prefers the entry under the priority-derived key, otherwise falls back to the first stored entry
 * (handles historically or manually mis-keyed data).
 */
export const resolveCurrentRecognition = (
  qualificationRecognition?: DetailedQualificationRecognition | null,
  priority?: CandidatePriority | null,
): QualificationRecognition | undefined => {
  if (!qualificationRecognition) return undefined;

  const currentQuestion = resolveCurrentRecognitionQuestion(qualificationRecognition, priority);
  const currentRecognition = currentQuestion ? qualificationRecognition[currentQuestion] : undefined;

  return currentRecognition?.status ? currentRecognition : undefined;
};

/**
 * Merges an incoming recognition update into the persisted one, per qualification question.
 *
 * Recognition is stored per qualification, so a candidate can hold a B and a D value at the same
 * time and updating one must never touch the other. A question missing from the update means "not
 * edited" and keeps its persisted value; a question present without a status means the user cleared
 * it and drops the entry.
 */
export const mergeQualificationRecognition = (
  current?: DetailedQualificationRecognition | null,
  update?: DetailedQualificationRecognition | null,
): DetailedQualificationRecognition | undefined => {
  if (!update) return current ?? undefined;

  const merged: DetailedQualificationRecognition = { ...current };

  for (const question of QUESTIONS_WITH_QUALIFICATION_RECOGNITION) {
    if (!(question in update)) continue;

    const recognition = update[question];
    if (recognition?.status) merged[question] = recognition;
    else delete merged[question];
  }

  return merged;
};

/**
 * Whether an incoming recognition update changes a status the prioritization depends on.
 * Only statuses are compared: dates do not take part in prioritization and would otherwise report a
 * change for every save, as they come back as strings from the client.
 */
export const hasChangedRecognitionStatus = (
  current?: DetailedQualificationRecognition | null,
  update?: DetailedQualificationRecognition | null,
): boolean => {
  if (!update) return false;

  return QUESTIONS_WITH_QUALIFICATION_RECOGNITION.some(
    question => question in update && update[question]?.status !== current?.[question]?.status,
  );
};
